import { ArrowLeft, ArrowRight, BookOpen, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { HELP_CATEGORIES } from "./articleKit";
import { HELP_ARTICLES } from "./helpArticles";

/**
 * /help - the knowledge base index. Categorized article cards with a
 * client-side search over titles, summaries, and keywords.
 */
export const HelpCenter = () => {
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return HELP_ARTICLES;
    return HELP_ARTICLES.filter((a) =>
      [a.title, a.summary, a.category, ...(a.keywords ?? [])]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [query]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 flex flex-col gap-5 pb-16">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground self-start"
      >
        <ArrowLeft className="w-3 h-3" /> Back to CRM
      </Link>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <BookOpen className="w-5 h-5" /> Help center
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            How Obvious works, from first hour to full stack. Every article
            links straight to the screens it describes.
          </p>
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
          <Input
            className="h-8 pl-7 text-sm w-64"
            placeholder="Search articles…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      {HELP_CATEGORIES.map((cat) => {
        const articles = visible.filter((a) => a.category === cat);
        if (articles.length === 0) return null;
        return (
          <div key={cat} className="flex flex-col gap-2">
            <h2 className="text-xs uppercase tracking-wide text-muted-foreground">
              {cat}
            </h2>
            <div className="grid md:grid-cols-2 gap-3">
              {articles.map((a) => (
                <Link key={a.slug} to={`/help/${a.slug}`} className="group">
                  <Card className="h-full transition-colors group-hover:border-primary/50">
                    <CardContent className="p-4 flex flex-col gap-1.5 h-full">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sm leading-snug">
                          {a.title}
                        </p>
                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5 group-hover:text-primary" />
                      </div>
                      <p className="text-xs text-muted-foreground leading-5 flex-1">
                        {a.summary}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
                        {a.minutes} min read
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        );
      })}
      {visible.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Nothing matches "{query}". Try a shorter word, or browse the
          categories above.
        </p>
      )}
    </div>
  );
};

HelpCenter.path = "/help";
